/**
 * Escapes a value for CSV format
 * Handles commas, quotes, and newlines by wrapping in quotes and escaping internal quotes
 * @param value - The value to escape (will be converted to string)
 * @returns Escaped CSV-safe string
 */
export function escapeCSVValue(value: string | number | null | undefined): string {
  // Convert to string, handling null/undefined
  const stringValue = value === null || value === undefined ? '' : String(value);

  // If value contains comma, quote, or newline, wrap in quotes and escape internal quotes
  if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n') || stringValue.includes('\r')) {
    // Replace each double quote with two double quotes
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
}

/**
 * Converts a 2D array of strings to CSV format
 * @param rows - 2D array where each inner array represents a row
 * @param includeBOM - Whether to include UTF-8 BOM for Excel compatibility (default: true)
 * @returns CSV formatted string
 */
export function convertArrayToCSV(rows: (string | number | null | undefined)[][], includeBOM: boolean = true): string {
  if (!rows || rows.length === 0) {
    return '';
  }

  // Convert each row to CSV format
  const csvRows = rows.map((row) => {
    // Escape each cell value and join with commas
    return row.map((cell) => escapeCSVValue(cell)).join(',');
  });

  // Join rows with newlines
  const csvContent = csvRows.join('\n');

  // Add UTF-8 BOM for Excel compatibility (helps with special characters)
  if (includeBOM) {
    return '\uFEFF' + csvContent;
  }

  return csvContent;
}

/**
 * Downloads a CSV string as a file
 * @param csvContent - The CSV content as a string
 * @param filename - The filename for the download (without .csv extension)
 */
export function downloadCSV(csvContent: string, filename: string): void {
  // Create blob with CSV content
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

  // Create download link
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';

  // Trigger download
  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
